/** @format */

import {useEffect, useMemo, useRef, useState} from 'react';

import {Alert, Box, LinearProgress} from '@mui/material';
import {add, format, isValid} from 'date-fns';
import {useSearchParams} from 'react-router-dom';

import {Table} from 'components/Table';

import {useSearchQuery} from '../context';
import {useGetCurrentUserSummaryQuery} from '../graphql/__generated__';
import {CompanySensorsMetricsFromQuery} from '../types';
import {summaryColumns} from './columns';
import {mapSummaryData} from './mapData';

const Summary = () => {
  const [searchParams] = useSearchParams();
  const searchQuery = useSearchQuery();
  const periodRange = searchParams.get('periodRange');

  const now = useRef(new Date()).current;

  const [startDate, setStartDate] = useState<Date>(() => add(now, {days: -31}));
  const [endDate, setEndDate] = useState<Date>(() => now);

  const {loading, data, error} = useGetCurrentUserSummaryQuery({
    variables: {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    },
  });

  useEffect(() => {
    const ranges = periodRange?.split(', ');
    if (ranges && ranges.length >= 2) {
      const parsedStart = new Date(ranges[0]);
      const parsedEnd = new Date(ranges[1]);
      if (isValid(parsedStart) && isValid(parsedEnd)) {
        setStartDate(parsedStart);
        setEndDate(parsedEnd);
      }
    }
  }, [periodRange]);

  const metrics = data?.currentUser?.companySensorMetrics;

  const tableData = useMemo(() => {
    const items = (metrics ?? []).filter(
      (item): item is CompanySensorsMetricsFromQuery => item != null,
    );
    return mapSummaryData(items);
  }, [metrics]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }

  if (loading || !metrics) {
    return (
      <Box sx={{width: '100%'}}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{mt: 4}}>
      <Table
        columns={summaryColumns}
        data={tableData}
        search={searchQuery || undefined}
        searchByColumnIds={['company']}
        defaultSortBy='company'
        defaultOrderBy='desc'
        variant='dark'
        stickyHeader
        columnResizingKey='chief-report-summary'
      />
    </Box>
  );
};

export default Summary;
